// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

import { SEED_ORG_PLACEHOLDER, SEED_WORKSPACE_PLACEHOLDER, coreSeeds } from './index'
import type { SeedSchema } from './constants'
import type { SchemaModel } from '../../models/Schema'

/**
 * Generate a deterministic schema ID from orgId + name.
 *
 * Unlike `createId('schemas')` which produces a random nanoid each call,
 * this always returns the same ID for the same (orgId, name) pair. This
 * makes seed operations idempotent: the `ON CONFLICT (id) DO UPDATE SET`
 * upsert at the server correctly updates existing schemas instead of
 * colliding with the (orgId, name) unique index.
 *
 * Uses djb2a hash × 2 seeds to produce a 10-char base36 suffix.
 */
function deterministicSchemaId(orgId: string, name: string): string {
  const djb2a = (str: string): number => {
    let hash = 5381
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) + hash) ^ str.charCodeAt(i)
    }
    return hash >>> 0
  }
  const key = `${orgId}:${name}`
  const h1 = djb2a(`${key}:0`)
  const h2 = djb2a(`${key}:1`)
  const combined = h1.toString(36) + h2.toString(36)
  return `schm_${combined.slice(0, 10)}`
}

/**
 * Result returned by {@link seedSchemas} describing what happened.
 */
export interface SeedResult {
  /** Number of schemas inserted during this run. */
  created: number
  /** Number of schemas that already existed and were left untouched. */
  skipped: number
}

/**
 * Options for controlling seed behavior.
 */
export interface SeedOptions {
  /**
   * Number of schemas to insert per batch before yielding.
   * Smaller batches produce smaller replication push payloads (local insert)
   * or smaller REST request bodies (REST write).
   * @default 5
   */
  batchSize?: number

  /**
   * Milliseconds to wait between batches so replication can flush.
   * @default 200
   */
  batchDelayMs?: number

  /**
   * Optional orgs collection for org-existence validation.
   * When provided, seedSchemas will verify the orgId exists before
   * inserting any schemas. If the org doesn't exist, returns immediately
   * with { created: 0, skipped: 0 }.
   * When omitted, no validation is performed (backward compatible).
   */
  orgsCollection?: {
    findOne(query: { selector: { id: string } }): {
      exec(): Promise<{ id: string } | null>
    }
  }

  /**
   * Custom seed list to use instead of the default `coreSeeds`.
   * Pass any subset of SeedSchema[] for targeted seeding.
   * When omitted, falls back to `coreSeeds` (shell infrastructure only).
   */
  seeds?: SeedSchema[]

  /**
   * When provided, missing schemas are written directly to the server via
   * this function instead of being inserted into the local RxDB collection.
   *
   * This avoids triggering replication push for bulk seed operations, which
   * causes RC_PUSH timeout errors when many schemas are created at once.
   * The caller is responsible for triggering a pull re-sync after seeding
   * to bring the server-side documents into the local IndexedDB.
   *
   * Receives a batch of fully-constructed {@link SchemaModel} documents.
   * Called once per batch; awaits completion before moving to the next.
   */
  writeViaRest?: (docs: SchemaModel[]) => Promise<void>
}

/**
 * Minimal interface for the RxDB schemas collection expected by the runner.
 * Using a structural interface instead of the full RxDB type so callers can
 * pass mocks in tests without dragging in the RxDB peer dependency.
 */
interface SchemasCollection {
  findOne(query: { selector: { name: string; orgId: string } }): {
    exec(): Promise<SchemaModel | null>
  }
  find(query: { selector: Record<string, unknown> }): {
    exec(): Promise<SchemaModel[]>
  }
  insert(doc: SchemaModel): Promise<unknown>
}

/**
 * Seeds built-in schema definitions into the schemas collection.
 *
 * Idempotent — fetches all existing schemas for the org in a single bulk
 * query and only inserts the ones that are missing. Safe to call on every
 * application startup.
 *
 * Placeholder values (`__SEED_ORG__`, `__SEED_WORKSPACE__`) in seed data
 * are replaced with the provided `orgId` and `workspaceId` before insert.
 *
 * Inserts are batched to avoid overwhelming replication with large push
 * payloads (which causes PayloadTooLargeError on the server).
 *
 * @param schemasCollection - An RxDB collection (or compatible mock) for schemas.
 * @param orgId - The target organisation ID; replaces {@link SEED_ORG_PLACEHOLDER}.
 * @param workspaceId - The target workspace ID; replaces {@link SEED_WORKSPACE_PLACEHOLDER}.
 * @param options - Optional batching configuration.
 * @returns Counts of created and skipped schemas.
 *
 * @example
 * const result = await seedSchemas(db.schemas, 'org_abc123', 'work_def456');
 * console.log(`created=${result.created} skipped=${result.skipped}`);
 */
export async function seedSchemas(
  schemasCollection: SchemasCollection,
  orgId: string,
  workspaceId: string,
  options?: SeedOptions
): Promise<SeedResult> {
  const batchSize = options?.batchSize ?? 5
  const batchDelayMs = options?.batchDelayMs ?? 200

  // Validate org exists if orgsCollection is provided.
  // This prevents seeding schemas for non-existent or placeholder orgs.
  if (options?.orgsCollection) {
    const org = await options.orgsCollection.findOne({ selector: { id: orgId } }).exec()
    if (!org) {
      console.warn(`[seedSchemas] Skipping: org "${orgId}" not found in orgs collection`)
      return { created: 0, skipped: 0 }
    }
  }

  // Resolve seed list — callers can pass a subset via options.seeds.
  const seeds = options?.seeds ?? coreSeeds

  // Bulk-fetch all existing schema names for this org in a single query.
  // This replaces N individual findOne() calls with 1 bulk find().
  const existingDocs = await schemasCollection.find({ selector: { orgId } }).exec()
  const existingNames = new Set(existingDocs.map(d => d.name))

  const missing = seeds.filter(seed => !existingNames.has(seed.name))
  const skipped = seeds.length - missing.length

  // Fast path: all schemas already exist — nothing to insert.
  if (missing.length === 0) {
    return { created: 0, skipped }
  }

  // Build all documents with resolved IDs, org/workspace, and timestamps.
  const now = new Date().toISOString()
  const docsToCreate: SchemaModel[] = missing.map(seed => ({
    ...seed,
    id: deterministicSchemaId(seed.orgId === SEED_ORG_PLACEHOLDER ? orgId : seed.orgId, seed.name),
    orgId: seed.orgId === SEED_ORG_PLACEHOLDER ? orgId : seed.orgId,
    workspaceId: seed.workspaceId === SEED_WORKSPACE_PLACEHOLDER ? workspaceId : seed.workspaceId,
    createdAt: now,
    updatedAt: now,
  }))

  let created = 0

  if (options?.writeViaRest) {
    // Write directly to the server REST API in batches.
    // Avoids local RxDB insert, which would trigger replication push with a
    // massive payload — the root cause of RC_PUSH timeout errors on seed.
    for (let i = 0; i < docsToCreate.length; i += batchSize) {
      const batch = docsToCreate.slice(i, i + batchSize)
      await options.writeViaRest(batch)
      created += batch.length

      // Yield between batches to avoid overwhelming the server
      if (i + batchSize < docsToCreate.length) {
        await new Promise(resolve => setTimeout(resolve, batchDelayMs))
      }
    }
  } else {
    // Original path: insert into local RxDB collection.
    // Replication push syncs each batch to the server.
    let batchCount = 0
    for (const doc of docsToCreate) {
      await schemasCollection.insert(doc)
      created++
      batchCount++

      // Yield between batches so replication can flush smaller push payloads
      if (batchCount >= batchSize) {
        batchCount = 0
        await new Promise(resolve => setTimeout(resolve, batchDelayMs))
      }
    }
  }

  return { created, skipped }
}
